import { DotsThree, EyeSlash, ImageSquare } from "@phosphor-icons/react";
import Link from "astro-typesafe-routes/link/react";
import plur from "plur";
import type React from "react";

import logo from "~/assets/logo.svg?url";
import { Avatar } from "~/components/Avatar";
import type { FragmentType } from "~/utils/fragment";
import { slugify } from "~/utils/slug";
import { getUserDisplayName } from "~/utils/user";

import { Menu, MenuButton } from "../Menu";
import { PackActions } from "../PackActions";
import { PackCoverImage } from "../PackCoverImage";
import type { PackCard_PackFragment } from "./PackCard_PackFragment";

export const PackCard: React.FC<{
  isLink?: boolean;
  pack: FragmentType<"Pack", { select: typeof PackCard_PackFragment }>;
  packActions?: Pick<React.ComponentProps<typeof PackActions>, "omit">;
}> = ({ isLink = true, pack, packActions }) => {
  return (
    <div className="relative pack">
      <div className="flex grow flex-col overflow-hidden rounded-xl border bg-base-100 shadow-sm">
        {pack.image && (
          <div className="relative aspect-video">
            <PackCoverImage image={pack.image} color={pack.color} />
          </div>
        )}
        <div className="flex grow flex-col justify-between space-y-2 p-4">
          <div className="space-y-1">
            {pack.collection && (
              <div className="truncate text-sm leading-tight">
                {pack.collection.name}
              </div>
            )}
            <div className="line-clamp-2 flex justify-between gap-2 font-medium leading-tight after:size-6">
              {isLink ? (
                <Link
                  className="before:absolute before:inset-0"
                  to="/packs/[slug]"
                  params={{
                    slug: slugify(pack),
                  }}
                >
                  {pack.name}
                </Link>
              ) : (
                pack.name
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pack.original ? (
              <img src={logo} alt="Knoword logo" className="size-5" />
            ) : (
              <Avatar size="sm" user={pack.user} />
            )}
            <div className="w-0 grow truncate text-sm">
              {pack.original ? "Knoword" : getUserDisplayName(pack.user)}
            </div>
            <div className="flex items-center gap-1">
              {pack.private && <EyeSlash weight="fill" className="size-4" />}
              {
                // packs with at least one image clue get the image badge
                pack.words.some(({ word }) =>
                  // TODO: perhaps we could share this logic between here and the game
                  word.definitions.some((definition) => definition.image),
                ) && <ImageSquare weight="bold" className="size-4" />
              }
              <div className="badge badge-sm ml-auto shrink-0">
                {pack.words.length} {plur("term", pack.words.length)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute right-2 top-2">
        <Menu>
          <MenuButton className="btn btn-circle btn-sm">
            <DotsThree weight="bold" className="size-6" />
          </MenuButton>
          <PackActions pack={pack} {...packActions} />
        </Menu>
      </div>
    </div>
  );
};
